/**
 * Constraint Priority — lightweight MVP.
 *
 * Users don't just pick a suburb by "what kind of person am I" (Lens). They
 * also pick by *what qualitative concern is non-negotiable today*: school
 * access, commute, or lifestyle. Lens and Priority are orthogonal — a Family
 * lens with School priority reads differently from a Family lens with
 * Commute priority.
 *
 * Why "Budget" is NOT here anymore:
 *   Budget previously appeared as a Priority pill AND as a separate Budget
 *   Band selector. Users read that as two redundant controls and bounced.
 *   Mental model fix: Priority = *qualitative* non-negotiable (school /
 *   commute / lifestyle); Budget Band = *quantitative* wallet scale. Users
 *   should read the combination as "I care about school AND I have this
 *   budget" rather than "I choose budget as a priority". The budget-band
 *   control is the single surface for wallet, and it composes with School
 *   or Commute to form the intersection layers.
 *
 * This module mirrors the decision-lens architecture:
 *   - single module-level `current` state, persisted in localStorage
 *   - `subscribe(listener)` fan-out so open cards refresh on change
 *   - `buildPriorityRow()` renders the pill group inside the Decide surface
 *
 * Design constraints from product brief:
 *   - No dashboards, no new sections, no heavy data systems
 *   - No real school rankings yet — proxies via existing tags/reasons
 *   - Default = "none" so opinionated Lens output stays the baseline
 *   - 4 pills total (None / School / Commute / Lifestyle) — small footprint
 */

export type Priority = "none" | "school" | "commute" | "lifestyle";

const STORAGE_KEY = "hyperlocal-priority";

let current: Priority = loadInitial();
const listeners = new Set<(p: Priority) => void>();

function loadInitial(): Priority {
  try {
    if (typeof localStorage !== "undefined") {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v === "school" || v === "commute" || v === "lifestyle" || v === "none") return v;
      // Migration: old "budget" priority is collapsed to "none" so existing
      // users don't get stuck on a pill that no longer exists. They can set
      // a Budget Band directly — which is what they actually meant.
      if (v === "budget") return "none";
    }
  } catch {
    /* storage blocked — fall through */
  }
  return "none";
}

export function getPriority(): Priority {
  return current;
}

export function getPriorityLabel(p: Priority = current): string {
  if (p === "school") return "School";
  if (p === "commute") return "Commute";
  if (p === "lifestyle") return "Lifestyle";
  return "None";
}

export function setPriority(p: Priority): void {
  if (p === current) return;
  current = p;
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, p);
    }
  } catch {
    /* ignore */
  }
  for (const fn of Array.from(listeners)) {
    try {
      fn(p);
    } catch {
      /* a broken subscriber must not break the rest */
    }
  }
}

export function subscribePriority(fn: (p: Priority) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/* -------------------------------------------------------------------------- */
/*  Accent per priority — used by card badges so the active constraint        */
/*  reads at a glance alongside the Lens badge.                               */
/* -------------------------------------------------------------------------- */

const PRIORITY_ACCENT: Record<Priority, { badge: string; badgeFg: string }> = {
  none:      { badge: "#4a4a4a", badgeFg: "#e6e6e6" },
  school:    { badge: "#6FB37A", badgeFg: "#0b1a0e" },
  commute:   { badge: "#9BA8E6", badgeFg: "#0f1020" },
  // Lifestyle — warm coral so it reads distinct from the green (school) and
  // blue (commute) pills without competing with the lens badge palette.
  lifestyle: { badge: "#E8A87C", badgeFg: "#1e0f08" },
};

export function getPriorityAccent(p: Priority = current) {
  return PRIORITY_ACCENT[p];
}

/* -------------------------------------------------------------------------- */
/*  Row builder — the Decide surface renders this directly into its "What     */
/*  matters most?" section. (The old fixed-position rail that mounted at      */
/*  boot only to be permanently CSS-hidden was deleted 2026-06-12 — see       */
/*  docs/ONE_STORY.md.)                                                       */
/* -------------------------------------------------------------------------- */

/**
 * None / School / Commute / Lifestyle radiogroup, store-wired. Build once
 * per page — the subscription lives for the session, matching the
 * persistent Decide surface DOM.
 */
export function buildPriorityRow(): HTMLElement {
  const row = document.createElement("div");
  row.setAttribute("role", "radiogroup");
  row.setAttribute("aria-label", "Constraint priority");
  row.style.cssText = `
    display:inline-flex;gap:3px;padding:3px;
    background:rgba(33,33,33,0.75);
    backdrop-filter:blur(10px);
    border:1px solid rgba(255,255,255,0.08);
    border-radius:999px;
    font:600 11.5px system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
    letter-spacing:0.02em;
  `;

  // Order: None → School → Commute → Lifestyle. "Budget" is intentionally
  // absent from the Priority row — wallet scale lives in the Budget Band
  // control below. Users shouldn't have to decide between "Budget priority"
  // and "pick a budget band" — there's only one budget surface now.
  const modes: Priority[] = ["none", "school", "commute", "lifestyle"];
  const pills: { p: Priority; btn: HTMLButtonElement }[] = [];

  const paint = () => {
    for (const { p, btn } of pills) {
      const active = p === current;
      const accent = PRIORITY_ACCENT[p];
      btn.setAttribute("aria-checked", String(active));
      btn.style.background = active
        ? accent.badge
        : "transparent";
      btn.style.color = active ? accent.badgeFg : "#d6d6d6";
      btn.style.fontWeight = active ? "700" : "500";
      btn.style.cursor = active ? "default" : "pointer";
      btn.style.boxShadow = active ? "0 1px 3px rgba(0,0,0,0.35)" : "none";
    }
  };

  for (const p of modes) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.setAttribute("role", "radio");
    btn.setAttribute("aria-label", `Priority: ${getPriorityLabel(p)}`);
    btn.textContent = getPriorityLabel(p);
    btn.style.cssText = `
      border:0;
      padding:5px 11px;
      border-radius:999px;
      font:inherit;
      transition:background 140ms ease,color 140ms ease;
    `;
    btn.addEventListener("click", () => {
      if (p === current) return;
      setPriority(p);
    });
    btn.addEventListener("mouseenter", () => {
      if (p !== current) btn.style.background = "rgba(255,255,255,0.08)";
    });
    btn.addEventListener("mouseleave", () => {
      if (p !== current) btn.style.background = "transparent";
    });
    pills.push({ p, btn });
    row.appendChild(btn);
  }

  paint();
  subscribePriority(() => paint());

  return row;
}
